<template>
    <div class="portfolio-page" :class="{ 'portfolio-page-embedded': embedded }">
        <nav v-if="!embedded && !publicMode" class="pf-nav">
            <button type="button" class="pf-back-btn" @click="goBackToProjects">← Back to Projects</button>
            <span class="pf-nav-title">Portfolio</span>
            <button type="button" class="doc-btn pf-nav-print" @click="copyPublicLink">
                Share
            </button>
        </nav>

        <CredentialStatus v-if="loadStatus !== 'ready'" :error="loadError" @retry="fetchData" />
        <div v-else class="doc-stage">
            <div id="showPortfolio" class="doc-print-root">
                <article class="doc-card portfolio-doc">
                    <header class="pf-hero">
                        <span v-if="portfolio.ideaTrack" class="pf-track-pill">{{ portfolio.ideaTrack }}</span>
                        <h1 class="pf-hero-title">{{ portfolio.projectTitle || 'Project Portfolio' }}</h1>
                        <div v-if="portfolio.members.length" class="memberRow">
                            <div
                                v-for="(member, index) in portfolio.members"
                                :key="index"
                                class="memberMini"
                            >
                                <div class="memberAvatar">
                                    <img v-if="member.avatarUrl && !failedAvatars[member.avatarUrl]" :src="member.avatarUrl" :alt="member.name" @error="$set(failedAvatars, member.avatarUrl, true)">
                                    <div v-else class="memberAvatarFallback">
                                        {{ member.initials || memberInitial(member.name) }}
                                    </div>
                                </div>
                                <div class="memberCopy">
                                    <div class="memberName">{{ formatMemberName(member.name) }}</div>
                                    <div v-if="member.role" class="memberRole">{{ member.role }}</div>
                                </div>
                            </div>
                        </div>
                    </header>

                    <div class="pf-body">
                        <main class="pf-main">
                            <section class="pf-section">
                                <h2 class="pf-sec-title">Project Introduction</h2>
                                <div v-if="portfolio.introduction" class="richText" v-html="portfolio.introduction"></div>
                                <p v-else class="portfolioPlaceholder">{{ emptyNarrativeTip }}</p>
                            </section>

                            <section v-if="portfolio.summaryVideoUrl" class="pf-section">
                                <h2 class="pf-sec-title">Summary Video</h2>
                                <div class="videoWrap">
                                    <video :src="portfolio.summaryVideoUrl" controls preload="metadata"></video>
                                </div>
                            </section>

                            <section class="pf-section">
                                <h2 class="pf-sec-title">Project Approach</h2>
                                <div v-if="portfolio.approach" class="richText" v-html="portfolio.approach"></div>
                                <p v-else class="portfolioPlaceholder">{{ emptyNarrativeTip }}</p>
                            </section>

                            <section v-if="portfolio.ideaBoardImages.length" class="pf-section">
                                <h2 class="pf-sec-title">Idea Boards</h2>
                                <div class="ideaBoardGrid">
                                    <a
                                        v-for="(img, index) in portfolio.ideaBoardImages"
                                        :key="index"
                                        class="ideaBoardCell"
                                        :href="img"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        <img :src="img" :alt="'Idea Board ' + (index + 1)">
                                        <span class="ideaBoardHint">Open</span>
                                    </a>
                                </div>
                            </section>

                            <section class="pf-section pf-section-last">
                                <h2 class="pf-sec-title">Impact &amp; Recognition</h2>
                                <div v-if="portfolio.highlightsImpacts" class="richText" v-html="portfolio.highlightsImpacts"></div>
                                <p v-else class="portfolioPlaceholder">{{ emptyNarrativeTip }}</p>
                            </section>
                        </main>

                        <aside class="pf-sidebar">
                            <section class="pf-side-card">
                                <h2 class="pf-side-title">Project Metadata</h2>
                                <dl class="pf-meta-list">
                                    <div v-if="portfolio.projectCycle" class="pf-meta-row">
                                        <dt>Cycle</dt>
                                        <dd>{{ portfolio.projectCycle }}</dd>
                                    </div>
                                    <div v-if="portfolio.ideaTrack" class="pf-meta-row">
                                        <dt>Track</dt>
                                        <dd>
                                            {{ portfolio.ideaTrack }}
                                            <span v-if="portfolio.innovationName && portfolio.innovationName !== portfolio.ideaTrack" class="pf-meta-sub">Focus: {{ portfolio.innovationName }}</span>
                                        </dd>
                                    </div>
                                    <div class="pf-meta-row">
                                        <dt>Result / Award</dt>
                                        <dd>{{ portfolio.awardLabel || 'Not Awarded' }}</dd>
                                    </div>
                                </dl>
                            </section>

                            <section v-if="portfolio.ethicsSummary" class="pf-side-card">
                                <h2 class="pf-side-title">Ethics Summary</h2>
                                <div class="pf-side-text" v-html="portfolio.ethicsSummary"></div>
                            </section>

                            <section v-if="!publicMode" class="pf-side-card">
                                <h2 class="pf-side-title">Share Portfolio</h2>
                                <label class="pf-link-label" for="portfolio-public-url">Public portfolio link</label>
                                <input id="portfolio-public-url" class="pf-public-link" :value="portfolioPublicUrl" readonly @focus="$event.target.select()">
                                <button type="button" class="pf-action pf-action-primary pf-copy-btn" @click="copyPublicLink">
                                    Copy Link
                                </button>
                            </section>

                            <div class="pf-side-actions">
                                <a
                                    v-if="portfolio.ethicsUrl"
                                    class="pf-action pf-action-secondary"
                                    :href="portfolio.ethicsUrl"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >View Ethics Statement</a>
                                <button
                                    v-if="hasReflection"
                                    type="button"
                                    class="pf-action pf-action-secondary"
                                    @click="openReflection"
                                >View Team Reflection</button>
                            </div>
                        </aside>
                    </div>
                </article>
            </div>
        </div>

        <div v-if="reflectionOpen" class="reflectionOverlay" @click.self="closeReflection">
            <section class="reflectionModal" role="dialog" aria-modal="true" aria-label="Team Reflection">
                <header class="reflectionHead">
                    <h2>Team Reflection</h2>
                    <button type="button" class="reflectionClose" @click="closeReflection">×</button>
                </header>
                <div class="reflectionTabs">
                    <button
                        v-for="tab in portfolio.reflectionTabs"
                        :key="tab.key"
                        type="button"
                        class="reflectionTab"
                        :class="{ active: activeReflectionTab === tab.key }"
                        @click="activeReflectionTab = tab.key"
                    >{{ tab.label }}</button>
                </div>
                <div class="reflectionBody" v-html="activeReflectionHtml"></div>
            </section>
        </div>
    </div>
</template>

<script>
import { getStudentPortfolioByApplication } from '../service'
import CredentialStatus from './CredentialStatus.vue'
import { normalizePortfolioPayload, memberInitials } from '../common/portfolio'

export default {
    name: 'portfolio',
    components: { CredentialStatus },
    props: {
        publicMode: { type: Boolean, default: false },
        initialPayload: { type: Object, default: null },
        embedded: {
            type: Boolean,
            default: false
        },
        applicationId: {
            type: String,
            default: ''
        }
    },
    data() {
        return {
            loadStatus: 'loading',
            loadError: '',
            failedAvatars: {},
            id: this.applicationId || this.$route.query.id,
            awards: [],
            projectInfo: {},
            studentInfo: {},
            accountInfo: {},
            projectManage: {},
            application: {},
            submissionDate: '',
            members: [],
            portfolio: normalizePortfolioPayload({}),
            reflectionOpen: false,
            activeReflectionTab: ''
        }
    },
    computed: {
        hasAward() {
            const label = this.portfolio.awardLabel || ''
            return label && label !== 'Not Awarded'
        },
        hasReflection() {
            return !!(this.portfolio.reflectionTabs && this.portfolio.reflectionTabs.length)
        },
        activeReflectionHtml() {
            const tabs = this.portfolio.reflectionTabs || []
            const active = tabs.find(tab => tab.key === this.activeReflectionTab) || tabs[0]
            return active ? active.html : ''
        },
        portfolioPublicUrl() {
            const hash = window.location.hash
            if (/^#\/portfolio(?:\?|$)/.test(hash)) return 'https://www.nextideamatters.com/' + hash
            return 'https://www.nextideamatters.com/#/portfolio?id=' + encodeURIComponent(this.id || '')
        },
        emptyNarrativeTip() {
            if (this.portfolio.generationStatus === 'pending') {
                return 'Portfolio narrative is being prepared from Project Summary.'
            }
            if (this.portfolio.generationStatus === 'failed') {
                return 'Portfolio narrative generation failed. Please try again later.'
            }
            if (this.portfolio.generationStatus === 'ready') {
                return 'This section was not included in the generated portfolio.'
            }
            return 'Content is not available yet.'
        }
    },
    watch: {
        applicationId(next) {
            if (next && next !== this.id) {
                this.id = next
                this.fetchData()
            }
        }
    },
    mounted() {
        this.fetchData()
    },
    methods: {
        goBackToProjects() {
            this.$router.push({ path: '/studentProjects' }).catch(() => {})
        },
        copyPublicLink() {
            const link = this.portfolioPublicUrl
            if (navigator.clipboard && navigator.clipboard.writeText) {
                navigator.clipboard.writeText(link).then(() => {
                    this.$message.success('Public link copied')
                }).catch(() => {
                    this.copyPublicLinkFallback(link)
                })
                return
            }
            this.copyPublicLinkFallback(link)
        },
        copyPublicLinkFallback(link) {
            const el = document.createElement('textarea')
            el.value = link
            el.setAttribute('readonly', 'readonly')
            el.style.position = 'fixed'
            el.style.left = '-9999px'
            document.body.appendChild(el)
            el.select()
            try {
                if (!document.execCommand('copy')) throw new Error('Copy unavailable')
                this.$message.success('Public link copied')
            } catch (e) {
                this.$message.warning('Copy failed')
            }
            document.body.removeChild(el)
        },
        resolveTrackLogo(codeOrName) {
            const raw = String(codeOrName || '').toUpperCase()
            if (!raw) {
                return ''
            }
            const map = {
                NSI: require('../assets/nsi_logo.png'),
                NRI: require('../assets/nri_logo.png'),
                NIR: require('../assets/nir_logo.png'),
                NIP: require('../assets/nip_logo.png'),
                NIA: require('../assets/nia_logo.png'),
                NIE: require('../assets/nie_logo.png'),
                NII: require('../assets/nii_logo.png'),
                NIV: require('../assets/niv_logo.png')
            }
            const keys = Object.keys(map)
            for (let i = 0; i < keys.length; i++) {
                const key = keys[i]
                if (raw.indexOf(key) >= 0) {
                    return map[key]
                }
            }
            return ''
        },
        memberInitial(name) {
            return memberInitials(name)
        },
        openReflection() {
            const tabs = this.portfolio.reflectionTabs || []
            if (!tabs.length) {
                return
            }
            this.activeReflectionTab = this.activeReflectionTab || tabs[0].key
            this.reflectionOpen = true
        },
        closeReflection() {
            this.reflectionOpen = false
        },
        formatMemberName(name) {
            return this.upfirstName(name)
        },
        async fetchData() {
            this.loadStatus = 'loading'; this.loadError = ''; this.$emit('load-state', 'loading')
            try {
                if (this.initialPayload) this.applyPortfolio(this.initialPayload)
                else {
                    const response = await getStudentPortfolioByApplication(this.id)
                    if (!response.data || response.data.code !== 0 || !response.data.data) throw new Error('Portfolio request failed')
                    this.applyPortfolio(response.data.data)
                }
                this.loadStatus = 'ready'; this.$emit('load-state', 'ready')
            } catch (error) {
                this.loadError = 'Please try again. Your portfolio could not be retrieved.'
                this.loadStatus = 'error'; this.$emit('load-state', 'error')
            }
        },
        applyPortfolio(payload) {
            // Prefer portfolio API members (team_credits). Do not override with awards roster.
            this.portfolio = normalizePortfolioPayload(payload, {
                awards: this.awards,
                projectInfo: this.projectInfo,
                projectManage: this.projectManage,
                application: this.application,
                submissionDate: this.submissionDate,
                members: (payload && (payload.members || payload.team_members)) || []
            })
            const tabs = this.portfolio.reflectionTabs || []
            this.activeReflectionTab = tabs.length ? tabs[0].key : ''
        },
        upfirstName(name) {
            if (!name) {
                return ''
            }
            if (name == 'João Pedro' || name == 'AGYEN-FREMPONG') {
                return name
            }
            return name.toLowerCase().replace(/\b([\w|‘]+)\b/g, function (word) {
                return word.replace(word.charAt(0), word.charAt(0).toUpperCase())
            })
        },
        exportPDF() {
            window.print()
        }
    }
}
</script>

<style lang="scss">
.portfolio-page {
    --sans: "Segoe UI", "Helvetica Neue", Helvetica, Arial, sans-serif;
    --serif: Georgia, "Times New Roman", Times, serif;
    --mono: "SF Mono", "Fira Code", Consolas, "Courier New", monospace;
    --navy: #073A67;
    --navy-deep: #052844;
    --blue: #0B6E99;
    --orange: #E8940A;
    --ink: #1A2433;
    --ink-2: #5A6678;
    --ink-3: #7A8798;
    --ink-4: #9AA6B5;
    --surf: #EEF2F6;
    --surf-2: #F7F9FC;
    --line: #E2E8F0;
    --bdr: #D0D8E2;
    --white: #FFFFFF;
    --wash: #F4F7FA;
    --accent-soft: rgba(232, 148, 10, 0.12);

    box-sizing: border-box;
    margin: -60px 0 0;
    min-height: calc(100vh + 60px);
    background:
        radial-gradient(1200px 420px at 12% -10%, rgba(7, 58, 103, 0.10), transparent 60%),
        radial-gradient(900px 360px at 90% 0%, rgba(232, 148, 10, 0.08), transparent 55%),
        var(--surf);
    color: var(--ink);
    font-family: var(--sans) !important;
    text-align: left !important;
    -webkit-font-smoothing: antialiased;
}

.portfolio-page *,
.portfolio-page *::before,
.portfolio-page *::after {
    box-sizing: border-box;
}

.pf-nav {
    background: rgba(255, 255, 255, 0.92);
    backdrop-filter: blur(8px);
    border-bottom: 1px solid var(--line);
    height: 52px;
    display: flex;
    align-items: center;
    padding: 0 1.5rem;
    position: sticky;
    top: 0;
    z-index: 200;
}

.pf-back-btn {
    appearance: none;
    border: none;
    background: transparent;
    font-size: 0.625rem;
    font-weight: 800;
    letter-spacing: 0.08em;
    color: var(--navy);
    flex: 1;
    min-width: 0;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    cursor: pointer;
    text-align: left;
}

.pf-nav-title {
    font: 600 0.8125rem / 1 var(--sans);
    color: var(--navy);
    border-bottom: 2px solid var(--orange);
    height: 52px;
    display: inline-flex;
    align-items: center;
    letter-spacing: 0.04em;
    flex-shrink: 0;
    padding: 0 1.1rem;
}

.pf-nav-print,
.doc-btn {
    flex-shrink: 0;
    margin-left: 0.75rem;
    background: var(--white);
    border: 1px solid var(--bdr);
    border-radius: 999px;
    padding: 0.4rem 1rem;
    font: 600 0.7rem / 1 var(--sans);
    color: var(--ink-2);
    cursor: pointer;
    letter-spacing: 0.02em;
    transition: border-color 0.15s, color 0.15s, background 0.15s;

    &:hover {
        border-color: var(--orange);
        color: var(--navy);
        background: #FFFBF3;
    }
}

.doc-stage {
    min-height: calc(100vh - 52px);
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 2.25rem 1rem 5rem;
    gap: 1rem;
}

.doc-print-root {
    width: 100%;
    max-width: 920px;
    display: flex;
    flex-direction: column;
    align-items: center;
}

.doc-card {
    width: 100%;
    max-width: 920px;
    background: var(--white);
    --navy: #073A67;
    --orange: #E8940A;
    --ink: #1A2433;
    --ink-2: #5A6678;
    --ink-3: #7A8798;
    --ink-4: #9AA6B5;
    --line: #E2E8F0;
    --wash: #F4F7FA;
    box-shadow:
        0 1px 2px rgba(7, 58, 103, 0.04),
        0 18px 48px rgba(7, 58, 103, 0.12);
    display: flex;
    flex-direction: column;
    text-align: left;
    border: 1px solid rgba(7, 58, 103, 0.08);
    overflow: hidden;
}

.pf-hero {
    position: relative;
    padding: 2.25rem 2.75rem 2rem;
    background:
        linear-gradient(135deg, #073A67 0%, #0A4F86 52%, #0B6E99 100%);
    color: #FFF;
}

.pf-hero::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    height: 3px;
    background: linear-gradient(90deg, var(--orange) 0%, rgba(232, 148, 10, 0.2) 55%, transparent 100%);
}

.pf-hero-top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    margin-bottom: 1.5rem;
}

.logo-mark {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 34px;
    padding: 0 9px;
    border-radius: 4px;
    background: #FFF;

    img {
        height: 22px;
        width: auto;
        display: block;
    }
}

.pf-hero-track {
    height: 34px;
    width: auto;
    padding: 5px 8px;
    background: #FFF;
    border-radius: 4px;
}

.pf-hero-kicker {
    margin: 0 0 0.55rem;
    font-size: 0.62rem;
    font-weight: 700;
    letter-spacing: 0.2em;
    text-transform: uppercase;
    color: rgba(255, 255, 255, 0.62);
}

.pf-hero-title {
    margin: 0;
    font-family: var(--serif);
    font-weight: normal;
    font-size: clamp(1.35rem, 2.6vw, 1.85rem);
    line-height: 1.28;
    max-width: 38rem;
    color: #FFF;
}

.pf-hero-meta {
    margin-top: 1.15rem;
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
}

.pf-chip {
    display: inline-flex;
    align-items: center;
    padding: 0.28rem 0.7rem;
    border-radius: 999px;
    font-size: 0.68rem;
    font-weight: 600;
    letter-spacing: 0.04em;
    background: rgba(255, 255, 255, 0.14);
    color: #FFF;
    border: 1px solid rgba(255, 255, 255, 0.18);
}

.pf-chip-muted {
    color: rgba(255, 255, 255, 0.78);
}

.pf-chip-award {
    background: rgba(232, 148, 10, 0.22);
    border-color: rgba(232, 148, 10, 0.45);
    color: #FFE6B0;
}

.pf-body {
    padding: 2rem 2.75rem 1.5rem;
}

.pf-section {
    margin-bottom: 2.15rem;
    padding-bottom: 2rem;
    border-bottom: 1px solid var(--line);
}

.pf-section-last {
    margin-bottom: 0.35rem;
    padding-bottom: 0.5rem;
    border-bottom: none;
}

.pf-sec-hdr {
    display: flex;
    align-items: flex-start;
    gap: 0.9rem;
    margin-bottom: 1.15rem;
}

.pf-sec-index {
    flex-shrink: 0;
    width: 2.1rem;
    height: 2.1rem;
    border-radius: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font: 700 0.68rem / 1 var(--sans);
    letter-spacing: 0.04em;
    color: var(--navy);
    background: var(--wash);
    border: 1px solid var(--line);
    margin-top: 0.1rem;
}

.pf-sec-title {
    margin: 0;
    font-family: var(--serif);
    font-size: 1.2rem;
    font-weight: normal;
    color: var(--navy);
    line-height: 1.25;
}

.pf-sec-sub {
    margin: 0.25rem 0 0;
    font-size: 0.72rem;
    color: var(--ink-3);
    letter-spacing: 0.01em;
}

.pf-sec-tag {
    margin-left: auto;
    align-self: center;
    font-size: 0.58rem;
    font-weight: 700;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    padding: 0.22rem 0.6rem;
    border-radius: 999px;
    background: rgba(11, 110, 153, 0.08);
    color: var(--blue);
    white-space: nowrap;
}

.memberList {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(118px, 1fr));
    gap: 1.15rem 1rem;
}

.memberCard {
    text-align: center;
    padding: 0.85rem 0.5rem 0.95rem;
    border-radius: 10px;
    background: var(--surf-2);
    border: 1px solid var(--line);
    transition: border-color 0.15s, transform 0.15s;

    &:hover {
        border-color: rgba(232, 148, 10, 0.45);
        transform: translateY(-1px);
    }
}

.memberAvatar {
    width: 72px;
    height: 72px;
    margin: 0 auto 0.65rem;
    border-radius: 50%;
    overflow: hidden;
    background: #FFF;
    border: 2px solid rgba(7, 58, 103, 0.12);
    box-shadow: 0 4px 14px rgba(7, 58, 103, 0.08);

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        display: block;
    }
}

.memberAvatarFallback {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    font: 700 1.15rem / 1 var(--sans);
    letter-spacing: 0.04em;
    color: var(--navy);
    background: linear-gradient(160deg, #EEF5FA 0%, #E8F0F7 100%);
}

.memberName {
    font-size: 0.8rem;
    font-weight: 650;
    line-height: 1.3;
    color: var(--ink);
    word-break: break-word;
}

.memberRole {
    margin-top: 0.2rem;
    font-size: 0.6rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--orange);
}

.videoWrap {
    border-radius: 12px;
    overflow: hidden;
    background: #071A2C;
    border: 1px solid rgba(7, 58, 103, 0.18);
    box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.04);

    video {
        width: 100%;
        max-height: 420px;
        display: block;
        background: #071A2C;
    }
}

.richText {
    font: 0.95rem / 1.78 var(--serif);
    color: var(--ink);
    text-align: justify;
    padding: 1rem 1.15rem;
    border-left: 3px solid var(--orange);
    background: linear-gradient(90deg, rgba(232, 148, 10, 0.05), transparent 55%);
    border-radius: 0 8px 8px 0;

    p {
        margin: 0 0 0.85rem;
    }

    p:last-child {
        margin-bottom: 0;
    }
}

.ideaBoardGrid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0.95rem;
    margin-top: 0.35rem;
}

.ideaBoardCell {
    position: relative;
    aspect-ratio: 4 / 3;
    border: 1px solid var(--line);
    border-radius: 10px;
    overflow: hidden;
    background: var(--wash);
    display: block;
    text-decoration: none;
    cursor: zoom-in;
    transition: transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease;

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        display: block;
        transition: transform 0.25s ease;
    }

    &:hover {
        border-color: rgba(7, 58, 103, 0.28);
        box-shadow: 0 10px 28px rgba(7, 58, 103, 0.12);
        transform: translateY(-2px);

        img {
            transform: scale(1.03);
        }

        .ideaBoardHint {
            opacity: 1;
            transform: translateY(0);
        }
    }
}

.ideaBoardHint {
    position: absolute;
    right: 0.7rem;
    bottom: 0.7rem;
    padding: 0.28rem 0.55rem;
    border-radius: 999px;
    background: rgba(7, 58, 103, 0.88);
    color: #FFF;
    font-size: 0.58rem;
    font-weight: 700;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    opacity: 0;
    transform: translateY(4px);
    transition: opacity 0.18s, transform 0.18s;
}

.portfolioPlaceholder {
    color: var(--ink-4);
    font: italic 0.9rem / 1.65 var(--serif);
    margin: 0;
    padding: 0.85rem 1rem;
    background: var(--wash);
    border-radius: 8px;
}

.pf-ft {
    border-top: 1px solid var(--line);
    padding: 1.1rem 2.75rem;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    flex-wrap: wrap;
    background: var(--wash);
}

.pf-ft-brand {
    display: flex;
    flex-direction: column;
    gap: 0.18rem;
}

.pf-ft-id {
    font: 600 0.625rem / 1 var(--mono);
    color: var(--ink-2);
    letter-spacing: 0.04em;
    font-variant-numeric: tabular-nums;
}

.pf-ft-lbl {
    font-size: 0.52rem;
    font-weight: 700;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--ink-4);
}

.pf-ft-track {
    height: 28px;
    width: auto;
    opacity: 0.92;
}

.pf-ft-r {
    text-align: right;
}

.pf-ft-date-lbl {
    font-size: 0.5rem;
    font-weight: 700;
    letter-spacing: 0.13em;
    text-transform: uppercase;
    color: var(--ink-4);
    margin-bottom: 0.15rem;
}

.pf-ft-date-val {
    font: 0.875rem / 1 var(--serif);
    color: var(--navy);
}

@media (max-width: 720px) {
    .pf-hero,
    .pf-body,
    .pf-ft {
        padding-left: 1.35rem;
        padding-right: 1.35rem;
    }

    .pf-hero {
        padding-top: 1.6rem;
        padding-bottom: 1.5rem;
    }

    .ideaBoardGrid {
        grid-template-columns: 1fr;
    }

    .pf-sec-hdr {
        flex-wrap: wrap;
    }

    .pf-sec-tag {
        margin-left: 0;
    }
}

@media print {
    .pf-nav {
        display: none !important;
    }

    .portfolio-page {
        margin: 0;
        min-height: auto;
        background: #FFF;
    }

    .doc-stage {
        padding: 0;
        min-height: auto;
        background: #FFF;
    }

    .doc-card {
        box-shadow: none !important;
        max-width: 100%;
        border: 1px solid #CBD5DF;
    }

    .ideaBoardHint {
        display: none !important;
    }
}

/* 2026 NIM credential portfolio refresh */
.portfolio-page {
    --navy: #2F509F;
    --amber: #F3A600;
    --yellow: #FFCA00;
    --sky: #67C3E8;
    --ink: #15181D;
    --ink-2: #515B68;
    --ink-3: #7A8491;
    --line: #E1E6EE;
    --wash: #F6F8FB;
    --surf: #EEF2F6;
}

.portfolio-page .doc-print-root,
.portfolio-page .doc-card {
    max-width: 1080px;
}

.portfolio-page .pf-hero {
    padding: 2rem 2.5rem 1.35rem;
    background: #FFFFFF;
    color: var(--ink);
}

.portfolio-page .pf-hero::after {
    background: var(--navy);
    height: 3px;
}

.portfolio-page .pf-track-pill {
    display: inline-flex;
    align-items: center;
    max-width: 100%;
    padding: 0.3rem 0.75rem;
    border: 1px solid rgba(47, 80, 159, 0.18);
    border-radius: 999px;
    background: rgba(103, 195, 232, 0.12);
    color: var(--navy);
    font-size: 0.66rem;
    font-weight: 800;
    letter-spacing: 0.08em;
    text-transform: uppercase;
}

.portfolio-page .pf-hero-title {
    margin: 0.8rem 0 0;
    max-width: 760px;
    font-family: var(--serif);
    font-size: clamp(1.8rem, 3vw, 2.55rem);
    line-height: 1.18;
    color: #15181D;
}

.portfolio-page .memberRow {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 0.55rem 0.75rem;
    margin-top: 1.25rem;
}

.portfolio-page .memberMini {
    min-width: 0;
    display: flex;
    align-items: center;
    gap: 0.55rem;
}

.portfolio-page .memberAvatar {
    width: 32px;
    height: 32px;
    margin: 0;
    border: 1px solid rgba(47, 80, 159, 0.16);
    box-shadow: none;
}

.portfolio-page .memberAvatarFallback {
    font-size: 0.72rem;
    color: var(--navy);
    background: #EFF5FB;
}

.portfolio-page .memberCopy {
    min-width: 0;
}

.portfolio-page .memberName {
    font-size: 0.74rem;
    font-weight: 700;
    color: var(--ink);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.portfolio-page .memberRole {
    margin-top: 0.08rem;
    font-size: 0.56rem;
    color: var(--ink-3);
}

.portfolio-page .pf-body {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 280px;
    gap: 2rem;
    padding: 2rem 2.5rem 2.5rem;
}

.portfolio-page .pf-main {
    min-width: 0;
}

.portfolio-page .pf-section {
    margin-bottom: 1.8rem;
    padding-bottom: 1.8rem;
}

.portfolio-page .pf-sec-title {
    margin: 0 0 0.9rem;
    font-family: var(--sans);
    font-size: 1.08rem;
    font-weight: 800;
    color: #15181D;
    border-left: 3px solid var(--navy);
    padding-left: 0.8rem;
}

.portfolio-page .richText {
    background: transparent;
    border-radius: 0;
    padding: 0.1rem 0;
    color: #15181D;
}

.portfolio-page .videoWrap {
    aspect-ratio: 16 / 9;
    border-radius: 6px;
    background: #101722;
}

.portfolio-page .videoWrap video {
    height: 100%;
    max-height: none;
    object-fit: contain;
}

.portfolio-page .ideaBoardGrid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.8rem;
}

.portfolio-page .ideaBoardCell {
    border-radius: 6px;
}

.portfolio-page .pf-sidebar {
    position: sticky;
    top: 70px;
    align-self: start;
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.portfolio-page .pf-side-card {
    border: 1px solid var(--line);
    border-radius: 6px;
    background: var(--wash);
    padding: 1rem;
}

.portfolio-page .pf-side-title {
    margin: 0 0 0.8rem;
    font-size: 0.78rem;
    font-weight: 800;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--navy);
}

.portfolio-page .pf-meta-list {
    margin: 0;
}

.portfolio-page .pf-meta-row {
    display: grid;
    grid-template-columns: 80px minmax(0, 1fr);
    gap: 0.7rem;
    padding: 0.55rem 0;
    border-top: 1px solid var(--line);
}

.portfolio-page .pf-meta-row:first-child {
    border-top: none;
    padding-top: 0;
}

.portfolio-page .pf-meta-row dt {
    color: var(--ink-3);
    font-size: 0.68rem;
    font-weight: 700;
}

.portfolio-page .pf-meta-row dd {
    margin: 0;
    color: var(--ink);
    font-size: 0.78rem;
    line-height: 1.45;
}

.portfolio-page .pf-meta-sub {
    display: block;
    margin-top: 0.18rem;
    color: var(--ink-3);
    font-size: 0.68rem;
}

.portfolio-page .pf-side-text {
    font: 0.82rem / 1.65 var(--serif);
    color: var(--ink-2);
}

.portfolio-page .pf-side-text p {
    margin: 0 0 0.65rem;
}

.portfolio-page .pf-side-text p:last-child {
    margin-bottom: 0;
}

.portfolio-page .pf-public-link {
    margin-bottom: 0.75rem;
    padding: 0.65rem 0.7rem;
    border: 1px solid var(--line);
    border-radius: 4px;
    background: #FFFFFF;
    color: var(--ink-2);
    font: 0.68rem / 1.45 var(--mono);
    overflow-wrap: anywhere;
}

.portfolio-page .pf-copy-btn {
    min-height: 36px;
}

.portfolio-page .pf-side-actions {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.portfolio-page .pf-action {
    width: 100%;
    min-height: 42px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 6px;
    border: 1px solid var(--navy);
    padding: 0.65rem 0.9rem;
    font-size: 0.78rem;
    font-weight: 800;
    text-decoration: none;
    cursor: pointer;
}

.portfolio-page .pf-action-primary {
    background: var(--navy);
    color: #FFFFFF;
}

.portfolio-page .pf-action-secondary {
    background: #FFFFFF;
    color: var(--navy);
}

.reflectionOverlay {
    position: fixed;
    inset: 0;
    z-index: 500;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 1rem;
    background: rgba(21, 24, 29, 0.46);
}

.reflectionModal {
    width: 100%;
    max-width: 780px;
    max-height: 82vh;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    border-radius: 8px;
    background: #FFFFFF;
    box-shadow: 0 24px 70px rgba(21, 24, 29, 0.22);
}

.reflectionHead {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    padding: 1rem 1.2rem;
    border-bottom: 1px solid var(--line);
}

.reflectionHead h2 {
    margin: 0;
    color: #15181D;
    font-size: 1rem;
}

.reflectionClose {
    border: none;
    background: transparent;
    color: var(--ink-3);
    font-size: 1.4rem;
    line-height: 1;
    cursor: pointer;
}

.reflectionTabs {
    display: flex;
    gap: 0.4rem;
    padding: 0.8rem 1.2rem 0;
    border-bottom: 1px solid var(--line);
}

.reflectionTab {
    border: none;
    border-bottom: 2px solid transparent;
    background: transparent;
    padding: 0.55rem 0.2rem 0.7rem;
    color: var(--ink-3);
    font-size: 0.78rem;
    font-weight: 800;
    cursor: pointer;
}

.reflectionTab.active {
    color: var(--navy);
    border-bottom-color: var(--navy);
}

.reflectionBody {
    overflow: auto;
    padding: 1.2rem;
    font: 0.92rem / 1.75 var(--serif);
    color: #15181D;
}

@media (max-width: 860px) {
    .portfolio-page .pf-body {
        grid-template-columns: 1fr;
    }

    .portfolio-page .pf-sidebar {
        position: static;
    }
}

@media (max-width: 720px) {
    .portfolio-page .pf-hero,
    .portfolio-page .pf-body {
        padding-left: 1.25rem;
        padding-right: 1.25rem;
    }
}

@media print {
    @page {
        size: A4;
        margin: 0;
    }

    html,
    body {
        margin: 0 !important;
        padding: 0 !important;
        background: #FFF !important;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
    }

    .portfolio-page .pf-body {
        grid-template-columns: 1fr 250px;
    }

    .portfolio-page .pf-sidebar {
        position: static;
    }

    .reflectionOverlay {
        display: none !important;
    }
}

.portfolio-page-embedded {
    margin: 0;
    min-height: auto;
    background: transparent;
}

.portfolio-page-embedded .doc-stage {
    min-height: auto;
}

.portfolio-page { --navy:#2F509F; --navy-deep:#2F509F; --teal:#67C3E8; --amber:#F3A600; --yellow:#FFCA00; --sky:#67C3E8; }
.portfolio-page .pf-hero { background:#2F509F; }
.portfolio-page .pf-action-primary { background:#2F509F; }
.portfolio-page .richText, .portfolio-page .richText p, .portfolio-page .pf-side-text, .portfolio-page .pf-side-text p { border-left:0 !important; }
.portfolio-page .pf-side-actions { display:flex; flex-direction:column; gap:16px; margin-top:0; }
.portfolio-page .pf-side-actions .pf-action { margin:0; }
</style>

<style>
.portfolio-page .pf-hero { color:#fff; }
.portfolio-page .pf-hero-title { color:#fff; font-size:clamp(1.85rem,3vw,2.55rem); line-height:1.2; max-width:920px; text-wrap:balance; }
.portfolio-page .pf-track-pill { color:#fff; background:#ffffff18; border-color:#ffffff40; }
.portfolio-page .memberName { color:#fff; font-size:.85rem; }
.portfolio-page .memberRole { color:#e0eaff; font-size:.7rem; line-height:1.4; }
.portfolio-page .memberAvatar { border:2px solid #ffffffb3; width:36px; height:36px; }
.portfolio-page .pf-hero::after { background:#F3A600; }
.portfolio-page .richText { text-align:left; line-height:1.75; }
.portfolio-page .richText p { text-align:left; }
</style>

<style>
.portfolio-page .pf-hero .memberRow { display:grid; grid-template-columns:repeat(3,minmax(0,1fr)); gap:20px 28px; }
.portfolio-page .pf-hero .memberMini { min-width:0; gap:10px; }
.portfolio-page .pf-hero .memberCopy { min-width:0; }
.portfolio-page .pf-hero .memberName { font-size:15px; line-height:1.4; overflow-wrap:anywhere; }
.portfolio-page .pf-hero .memberRole { font-size:12px; letter-spacing:.03em; overflow-wrap:anywhere; }
.portfolio-page .pf-hero .memberAvatar { flex-shrink:0; width:40px; height:40px; }
.portfolio-page .pf-link-label { display:block; font:14px/1.5 Arial,sans-serif; color:#53627b; margin-bottom:8px; }
.portfolio-page input.pf-public-link { display:block; box-sizing:border-box; width:100%; min-width:0; padding:10px; color:#344563; background:white; border:1px solid #d8e0eb; border-radius:4px; font:13px/1.4 Arial,sans-serif; text-overflow:ellipsis; white-space:nowrap; margin-bottom:12px; }
.portfolio-page .pf-public-link:focus { outline:2px solid #2F509F; outline-offset:2px; }
@media(max-width:700px) { .portfolio-page .pf-hero .memberRow { grid-template-columns:repeat(2,minmax(0,1fr)); } }
@media(max-width:440px) { .portfolio-page .pf-hero .memberRow { grid-template-columns:1fr; gap:16px; } }
</style>
